/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$radio-button-label-margin-top: 4px !default;
$radio-button-small-label-margin-top: 0px !default;
$radio-button-border-width: 2px !default;
$radio-button-small-border-width: 2px !default;
$radio-button-svg-size: 20px !default;
$radio-button-svg-size-small: 20x !default;
$radio-button-background-size: 40px !default;
$radio-button-label-margin-left: 8px !default; // distance between radio and the labels
$radio-button-border-size: 10px !default;
$radio-button-border-size-small: 7px !default;
$radio-button-label-line-height: 24px !default;
$radio-button-small-label-line-height: 18px !default;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

:host {
  display: inline-block;
}

:host([disabled]:not([disabled="false"])) {
  cursor: not-allowed;
  label {
    @include disabled();
  }
}

::slotted(eon-ui-rte-renderer) {
  --rteRendererLineHeight: #{$radio-button-label-line-height};
}

.#{$prefix}radio-button {
  .#{$prefix}eon-radio-button {
    position: relative;
    width: auto;
    max-width: 100%;
    min-height: $radio-button-svg-size;
    font-family: $font-family-base;
    cursor: pointer;
    outline: none;
    display: inline-flex;
    justify-content: flex-start;
    align-items: flex-start;
    box-sizing: content-box;
    margin-bottom: 0;
    font-size: 18px;
    line-height: 24px;

    &:hover {
      .#{$prefix}eon-radio-button-background {
        .#{$prefix}eon-radio-button-outer-circle:not(.eonui-checked, .eonui-disabled)::before {
          width: 10px;
          height: 10px;
          opacity: 0.5;
          transform: translate(-50%, -50%);
          background-color: v(uncheckedHoverColor) !important;
        }

        .#{$prefix}eon-radio-button-outer-circle.#{$prefix}checked:not(.eonui-disabled) {
          border-color: v(checkedHoverColor) !important;
        }
        .#{$prefix}eon-radio-button-outer-circle.#{$prefix}checked:not(.eonui-disabled)::before {
          background-color: v(checkedHoverColor);
        }

        .#{$prefix}eon-radio-button-outer-circle.#{$prefix}error {
          border-color: v(errorColor) !important;
        }
      }

      .#{$prefix}error {
        font-family: $copy-small-font-family;
        font-size: $copy-small-font-size;
        font-weight: $copy-small-font-weight;
        line-height: $copy-small-line-height;
        color: v(errorColor) !important;
      }
    }

    .#{$prefix}native-control {
      top: 0;
      right: 0;
      left: 0;
      width: $radio-button-svg-size;
      height: $radio-button-svg-size;
      position: absolute;
      opacity: 0;
      cursor: inherit;
      z-index: 1;
      margin: 0;
      padding: 0;
      flex-shrink: 0;
      flex-grow: 0;

      &:focus + .#{$prefix}eon-radio-button-background,
      &:focus-within + .#{$prefix}eon-radio-button-background {
        .#{$prefix}eon-radio-button-outer-circle.#{$prefix}checked {
          outline: 2px solid;
          outline-color: v(focusColor);
          border-color: v(checkedHoverColor) !important;
        }
        .#{$prefix}eon-radio-button-outer-circle.#{$prefix}checked::before {
          background-color: v(checkedHoverColor);
        }
      }
    }
    .#{$prefix}native-control:checked + .#{$prefix}eon-radio-button-background,
    .#{$prefix}native-control:disabled + .#{$prefix}eon-radio-button-background {
      transition: opacity 120ms cubic-bezier(0, 0, 0.2, 1) 0ms, transform 120ms cubic-bezier(0, 0, 0.2, 1) 0ms;
    }

    .#{$prefix}native-control:checked
      + .#{$prefix}eon-radio-button-background
      .#{$prefix}eon-radio-button-outer-circle {
      color: v(checkedColor);
      &::before {
        width: 10px;
        height: 10px;
        transform: translate(-50%, -50%);
        transition: all 0.2s cubic-bezier(0.35, 0.9, 0.4, 0.9);
        opacity: 1;
      }
    }

    .#{$prefix}eon-radio-button-background {
      display: inline-block;
      position: relative;
      box-sizing: border-box;
      width: $radio-button-svg-size;
      height: $radio-button-svg-size;
      flex-shrink: 0;
      flex-grow: 0;

      .#{$prefix}eon-radio-button-outer-circle {
        position: absolute;
        top: 0;
        left: 0;
        box-sizing: border-box;
        width: 100%;
        height: 100%;
        border-width: $radio-button-border-width;
        border-style: solid;
        border-radius: 50%;
        color: v(uncheckedColor);

        &::before {
          width: 0;
          height: 0;
          position: absolute;
          content: "";
          border-radius: 100%;
          left: 50%;
          top: 50%;
          background-color: var(--checkedColor);
          opacity: 0;
          transform: translate(-50%, -50%);
          transition: all 0.2s ease;
        }
      }

      .#{$prefix}eon-radio-button-outer-circle.#{$prefix}disabled-unchecked,
      .#{$prefix}eon-radio-button-outer-circle.#{$prefix}disabled {
        @include disabled();
      }

      .#{$prefix}eon-radio-button-outer-circle.#{$prefix}error {
        font-family: $copy-small-font-family;
        font-size: $copy-small-font-size;
        font-weight: $copy-small-font-weight;
        line-height: $copy-small-line-height;
        border-color: v(errorColor) !important;
        color: v(errorColor);

        &::before {
          background-color: v(errorColor) !important;
        }
      }
    }
    .#{$prefix}label {
      margin-left: $radio-button-label-margin-left;
      margin-top: calc((#{$radio-button-label-line-height} - #{$radio-button-svg-size})/-2);
      transition: color $transition-duration $transition-timing-function 0s;
      white-space: normal;
      @include noneUserSelect();
      color: v(labelColor);
      font-size: 18px;
      line-height: 24px;
    }

    .#{$prefix}label.#{$prefix}disabled {
      color: v(disabledLabelColor);
    }

    .#{$prefix}eon-radio-button-background::before {
      top: -10px;
      left: -10px;
      width: $radio-button-background-size;
      height: $radio-button-background-size;
      position: absolute;
      transform: scale(0, 0);
      opacity: 0;
      pointer-events: none;
      content: "";
      border-radius: 50%;
      transition: opacity 120ms cubic-bezier(0.4, 0, 0.6, 1) 0ms, transform 120ms cubic-bezier(0.4, 0, 0.6, 1) 0ms;
    }
    .#{$prefix}eon-radio-button-outer-circle {
      &.#{$prefix}disabled {
        color: v(disabledLabelColor) !important;

        &::before {
          background-color: v(disabledLabelColor) !important;
        }
      }
    }
  }
}
.#{$prefix}radio-button {
  display: flex;
}

.#{$prefix}radio-button-info-icon {
  align-items: flex-start;
  margin: 4px 16px 0;
}

.#{$prefix}radio-button {
  &.#{$prefix}small {
    .#{$prefix}eon-radio-button {
      min-height: unset;

      .#{$prefix}label {
        font-size: 16px;
        line-height: 24px;
        margin-left: 8px;
        margin-top: calc((#{$radio-button-small-label-line-height} - #{$radio-button-svg-size-small})/-2);
      }

      .#{$prefix}native-control {
        width: $radio-button-svg-size-small;
        height: $radio-button-svg-size-small;
      }

      .#{$prefix}eon-radio-button-background {
        width: $radio-button-svg-size-small;
        height: $radio-button-svg-size-small;

        .#{$prefix}eon-radio-button-outer-circle {
          border-width: $radio-button-small-border-width;

          &::before {
            width: 0;
            height: 0;
            position: absolute;
            content: "";
            border-radius: 100%;
            left: 50%;
            top: 50%;
            background-color: var(--checkedColor);
            opacity: 0;
            transform: translate(-50%, -50%);
            transition: all 0.2s ease;
          }
        }
      }

      .#{$prefix}native-control:checked
        + .#{$prefix}eon-radio-button-background
        .#{$prefix}eon-radio-button-outer-circle {
        &::before {
          width: 10px;
          height: 10px;
          transform: translate(-50%, -50%);
          transition: all 0.2s cubic-bezier(0.35, 0.9, 0.4, 0.9);
          opacity: 1;
        }
      }
    }
  }
}
